import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { Input, Icon } from '@material-ui/core';

import { CustomTooltip } from 'components/oldComponents';
import { capitalize } from 'utils';

import { useStyles } from './styles';

interface ITableSearchLabelProps {
  onSearch(text: string): void;
  placeholder?: string;
  searchParam?: string;
  searchId?: string;
  externalSearch?: string;
}

export const TableSearchLabel: React.FC<ITableSearchLabelProps> = props => {
  const [searchable, setSearchable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef(null);
  const classes = useStyles(props);
  const { children, onSearch, placeholder, searchParam, externalSearch } =
    props;
  const [searchText, setSearchText] = useState(externalSearch || '');

  const handleSearch = useCallback(
    (reset = false) => {
      const text = reset ? '' : inputRef.current?.value;

      onSearch(text || '');
      setSearchText(text || '');
      setSearchable(false);
    },
    [onSearch],
  );

  const toggleSearch = useCallback(
    (reset = false) => {
      setSearchable(!searchable);

      if (searchable) {
        handleSearch(reset);
      }

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    },
    [searchable, handleSearch],
  );

  const handleEnter = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.keyCode === 13) {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const formattedPlaceholder = useMemo(() => {
    return `Buscar ${capitalize(
      searchParam ? searchParam.replace(/(s)$/g, '') : '',
    )}`;
  }, [searchParam]);

  useEffect(() => {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        setSearchable(false);
        handleSearch();
      }
    });
  }, [handleSearch]);

  useEffect(() => {
    return () =>
      window.removeEventListener('keydown', (event: KeyboardEvent) => {
        if (event.code === 'Escape') {
          setSearchable(false);
          handleSearch();
        }
      });
  }, [handleSearch]);

  return (
    <span
      className={classes.container}
      style={{ width: searchable ? 300 : undefined }}
    >
      <div className={classes.labelContainer}>
        {!searchable ? (
          <span>{children}</span>
        ) : (
          <>
            <Input
              inputRef={inputRef}
              defaultValue={searchText}
              className={classes.searchInput}
              inputProps={{ 'aria-label': 'busca' }}
              placeholder={placeholder || formattedPlaceholder}
              onKeyUp={handleEnter}
            />

            <CustomTooltip
              ref={tooltipRef}
              labelValue="Aplicar filtro"
              minWidth={100}
              placement="bottom"
              showCircle={false}
            >
              <button
                className={`${classes.buttonSearch} ${classes.applyButton}`}
                type="button"
                aria-label="Aplicar filtro"
                onClick={() => handleSearch()}
              >
                <Icon>arrow_forward</Icon>
              </button>
            </CustomTooltip>

            <CustomTooltip
              ref={tooltipRef}
              labelValue="Remover filtro"
              minWidth={100}
              placement="bottom"
              showCircle={false}
            >
              <button
                className={`${classes.buttonSearch} ${classes.removeButton}`}
                type="button"
                aria-label="Remover filtro"
                onClick={() => toggleSearch(true)}
              >
                <Icon>delete_forever</Icon>
              </button>
            </CustomTooltip>
          </>
        )}

        <CustomTooltip
          ref={tooltipRef}
          labelValue={searchable ? 'Fechar busca' : 'Abrir busca'}
          minWidth={100}
          placement="bottom"
          showCircle={false}
        >
          <button
            className={classes.buttonSearch}
            type="button"
            aria-label={formattedPlaceholder}
            onClick={() => toggleSearch()}
          >
            {searchable ? (
              <Icon className={classes.closeIcon}>close</Icon>
            ) : (
              <Icon
                className={!searchText.length ? classes.searchIcon : ''}
                color={searchText.length ? 'primary' : 'inherit'}
              >
                search
              </Icon>
            )}
          </button>
        </CustomTooltip>
      </div>
    </span>
  );
};
